import { AlertTriangle, Clock } from 'lucide-react'
import { useState } from 'react'

import { privateAnalyticsProps } from '@/components/molecules/shared/sensitive-analytics'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { StripeApiService } from '@/lib/http/stripe-api.service'
import { cn } from '@/lib/utils'
import { SUPPORT_EMAIL } from '@/utils/constants'

import { useBillingHealth } from '../hooks/use-billing-health'

const DISMISS_KEY = 'billing-health-banner-dismissed-until'

function dismissedUntil(): number {
  try {
    return Number(window.localStorage.getItem(DISMISS_KEY) ?? 0)
  } catch {
    return 0
  }
}

function money(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100)
}

/**
 * One line at the top of every customer page while a subscription is past due,
 * an invoice needs confirmation, or a bank payment is still settling. "Hide for 24 hours"
 * hides it for 24 hours in this browser; the underlying state is the Worker's.
 */
export function BillingHealthBanner() {
  const { data } = useBillingHealth()
  const { toast } = useToast()
  const [hidden, setHidden] = useState(() => dismissedUntil() > Date.now())
  const [opening, setOpening] = useState(false)

  if (!data || hidden) return null
  const pastDue = data.pastDue[0]
  const action = data.actionRequired[0]
  const processing = data.processing[0]
  if (!pastDue && !action && !processing) return null

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000))
    } catch {
      // localStorage may be unavailable; the banner simply reappears next load.
    }
    setHidden(true)
  }

  const openPortal = async () => {
    setOpening(true)
    try {
      const { url } = await StripeApiService.BillingPortal()
      window.location.assign(url)
    } catch (error) {
      console.error('Billing portal failed to open:', error)
      toast({
        title: "We couldn't open billing",
        description: `Try again in a minute. If it keeps failing, write to ${SUPPORT_EMAIL}.`,
        variant: 'destructive',
      })
    } finally {
      setOpening(false)
    }
  }

  const urgent = Boolean(pastDue || action)
  return (
    <div
      data-testid="billing-health-banner"
      role="status"
      className={cn(
        'flex flex-wrap items-center justify-center gap-3 border-b px-4 py-2 text-sm',
        pastDue
          ? 'border-danger-border bg-danger-tint text-danger'
          : action
            ? 'border-warning-border bg-warning-tint text-warning'
            : 'border-border bg-surface text-body',
      )}
    >
      {urgent ? (
        <AlertTriangle className="size-4 shrink-0" aria-hidden />
      ) : (
        <Clock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      )}
      {pastDue ? (
        <span>
          Your payment of {money(pastDue.amount, pastDue.currency)} for <strong>{pastDue.serviceName}</strong>{' '}
          didn&apos;t go through.
        </span>
      ) : action ? (
        <span>
          Your bank needs you to confirm the payment for <strong>{action.serviceName}</strong>.
        </span>
      ) : (
        <span>
          Your bank payment for <strong>{processing!.serviceName}</strong> is processing. This usually takes 3–5
          business days.
        </span>
      )}
      {pastDue ? (
        <Button size="sm" variant="outline" onClick={openPortal} disabled={opening}>
          {opening ? 'Opening…' : 'Update payment method'}
        </Button>
      ) : null}
      {(pastDue?.hostedInvoiceUrl ?? action?.hostedInvoiceUrl) ? (
        <a
          className="text-sm font-medium underline"
          href={pastDue?.hostedInvoiceUrl ?? action?.hostedInvoiceUrl ?? '#'}
          {...privateAnalyticsProps}
          target="_blank"
          rel="noreferrer"
        >
          {pastDue ? 'Pay invoice' : 'Confirm payment'}
        </a>
      ) : null}
      <button type="button" onClick={dismiss} className="text-sm underline">
        Hide for 24 hours
      </button>
    </div>
  )
}
